/**
 * Coaching phase (whistle icon, 2026-08-06) — "how is this person
 * selling", vs Dashboards' "how are the deals doing".
 *
 * Two sub-tabs:
 *   1. Call habits — talk ratio + trend, longest monologue, question
 *      rate, computed server-side from diarized transcripts
 *      (GET /api/coaching/summary). Where the rep can't be identified
 *      on a call, we NEVER guess — the call renders in a "which one is
 *      you?" list; one click writes speaker_name_overrides via the
 *      existing rename endpoint, which also fixes the call-review
 *      timeline labels.
 *   2. QBR briefs — relocated out of Dashboards (a generated document
 *      isn't a live analytics view; the tab strip promised numbers and
 *      delivered a document list). Same component, unchanged.
 *
 * Visibility: nav item + backend route are both dark behind the
 * coaching_tab feature flag. Self-view always; the rep picker appears
 * only when the backend returns `team` (demos.view_team_reports).
 */

import { useCallback, useEffect, useState } from 'react';
import QbrBriefsPhase from './QbrBriefsPhase';

const API_BASE = process.env.REACT_APP_API_URL || 'https://trailercst-backend-production.up.railway.app';

const PERIODS = [
  { id: '30d', label: '30 days' },
  { id: '90d', label: '90 days' },
  { id: 'all', label: 'All time' },
];

const fmtClock = (secs) => {
  if (secs == null) return '—';
  const m = Math.floor(secs / 60);
  const s = Math.round(secs % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
};

// Same thresholds as Team Performance's talk-ratio column: under 45%
// healthy, 45-60 amber, above 60 flags a monologue-heavy style.
const ratioColor = (pct) => (pct == null ? 'var(--room-text-subtle, #94a3b8)' : pct > 60 ? '#dc2626' : pct > 45 ? '#d97706' : '#16a34a');

function StatTile({ label, value, sub, valueColor }) {
  return (
    <div className="dash-card" style={{ padding: '16px 18px', minWidth: 150, flex: '1 1 150px' }}>
      <div style={{ fontSize: 12, color: 'var(--room-text-muted, #64748b)', fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: 28, fontWeight: 700, color: valueColor || 'var(--room-text-strong, #0f172a)', marginTop: 4, fontVariantNumeric: 'tabular-nums' }}>
        {value}
      </div>
      {sub && <div style={{ fontSize: 11, color: 'var(--room-text-subtle, #94a3b8)', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

export default function CoachingPhase({ authToken, authFetch, authUser, onOpenCall }) {
  const [tab, setTab] = useState('habits'); // 'habits' | 'qbr'
  const [period, setPeriod] = useState('90d');
  const [userId, setUserId] = useState(null); // null = self
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [claiming, setClaiming] = useState(null); // demoId while a "that's me" is in flight

  const doFetch = useCallback((url, opts) => (authFetch
    ? authFetch(url, opts)
    : fetch(url, { ...opts, headers: { ...(opts && opts.headers), Authorization: `Bearer ${authToken}` } })), [authFetch, authToken]);

  const load = useCallback(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const q = userId ? `&userId=${userId}` : '';
    doFetch(`${API_BASE}/api/coaching/summary?period=${period}${q}`)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((j) => { if (!cancelled) { setData(j); setLoading(false); } })
      .catch((e) => { if (!cancelled) { setError(e.message); setLoading(false); } });
    return () => { cancelled = true; };
  }, [doFetch, period, userId]);

  useEffect(() => load(), [load]);

  // "That's me" — writes the caller's full name onto the chosen speaker
  // through the same endpoint the call-review rename uses, so both
  // surfaces improve from one click. Only offered on self-view: a
  // manager can't claim a speaker on someone else's behalf.
  const claimSpeaker = async (demoId, speakerId) => {
    const myName = (authUser && (authUser.fullName || authUser.email)) || '';
    if (!myName) return;
    setClaiming(demoId);
    try {
      const r = await doFetch(`${API_BASE}/api/demos/${demoId}/speakers/${encodeURIComponent(speakerId)}/name`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: myName }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      load();
    } catch (e) {
      setError(`Couldn't save (${e.message})`);
    } finally {
      setClaiming(null);
    }
  };

  const viewingSelf = !userId || (data && authUser && data.user && data.user.id === authUser.id);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10, marginBottom: 14 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 20 }}>Coaching</h2>
          <p style={{ margin: '2px 0 0', fontSize: 12, color: 'var(--room-text-muted, #64748b)' }}>
            Call habits, measured from your real conversations — plus QBR prep.
          </p>
        </div>
        <div style={{ display: 'inline-flex', padding: 4, gap: 4, background: 'var(--room-border-soft, #f1f5f9)', borderRadius: 12 }}>
          {[{ id: 'habits', label: 'Call habits' }, { id: 'qbr', label: 'QBR briefs' }].map((t) => {
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setTab(t.id)}
                style={{
                  padding: '8px 16px', borderRadius: 8, border: 'none', cursor: 'pointer',
                  background: active ? 'var(--room-card, #fff)' : 'transparent',
                  color: active ? 'var(--room-text-strong, #0f172a)' : 'var(--room-text-muted, #64748b)',
                  fontWeight: active ? 600 : 500, fontSize: 13, fontFamily: 'inherit',
                  boxShadow: active ? '0 1px 3px rgba(15, 23, 42, 0.08)' : 'none',
                }}
              >
                {t.label}
              </button>
            );
          })}
        </div>
      </div>

      {tab === 'qbr' && (
        <QbrBriefsPhase
          authToken={authToken}
          currentUserId={authUser?.id || null}
          isCompanyAdmin={!!(authUser?.company?.isAdmin)}
        />
      )}

      {tab === 'habits' && (
        <div>
          {/* Period + (manager-only) rep picker */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
            <div style={{ display: 'inline-flex', gap: 4, padding: 3, borderRadius: 10, background: 'var(--room-border-soft, #f1f5f9)', border: '1px solid var(--room-border, #e2e8f0)' }}>
              {PERIODS.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setPeriod(p.id)}
                  aria-pressed={period === p.id}
                  style={{
                    padding: '5px 10px', borderRadius: 7, border: 'none',
                    background: period === p.id ? 'var(--room-card, #fff)' : 'transparent',
                    color: period === p.id ? 'var(--room-text-strong, #0f172a)' : 'var(--room-text-muted, #64748b)',
                    fontWeight: 600, fontSize: 12, fontFamily: 'inherit', cursor: 'pointer',
                    boxShadow: period === p.id ? '0 1px 3px rgba(15,23,42,0.1)' : 'none',
                  }}
                >
                  {p.label}
                </button>
              ))}
            </div>
            {data && Array.isArray(data.team) && data.team.length > 1 && (
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 12, color: 'var(--room-text-muted, #64748b)' }}>Coaching:</span>
                <select
                  value={userId || ''}
                  onChange={(e) => setUserId(e.target.value ? Number(e.target.value) : null)}
                  style={{
                    padding: '7px 10px', borderRadius: 8, border: '1px solid var(--room-border, #e2e8f0)',
                    background: 'var(--room-card, #fff)', fontSize: 13, fontFamily: 'inherit', color: 'var(--room-text-body, #334155)', cursor: 'pointer',
                  }}
                >
                  <option value="">Myself</option>
                  {data.team.filter((m) => m.userId !== (authUser?.id)).map((m) => (
                    <option key={m.userId} value={m.userId}>{m.userName}</option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {loading && <p style={{ color: 'var(--room-text-muted, #64748b)' }}>Measuring call habits…</p>}
          {!loading && error && (
            <div className="dash-card" style={{ padding: 20 }}>
              <p style={{ color: 'var(--room-text-muted, #64748b)', margin: 0 }}>
                Couldn't load coaching data ({error}).{' '}
                <button type="button" onClick={load} style={{ border: 'none', background: 'none', color: '#6366f1', cursor: 'pointer', padding: 0, fontFamily: 'inherit', fontSize: 'inherit' }}>
                  Retry
                </button>
              </p>
            </div>
          )}

          {!loading && !error && data && (
            <>
              {/* Honest empty state — no diarized calls at all */}
              {data.summary.coachableCalls === 0 && (
                <div className="dash-card" style={{ padding: 24 }}>
                  <h3 style={{ margin: '0 0 6px', fontSize: 15 }}>No coachable calls in this period yet</h3>
                  <p style={{ margin: 0, fontSize: 13, color: 'var(--room-text-muted, #64748b)', lineHeight: 1.6 }}>
                    Habit metrics come from calls with a speaker-separated transcript
                    (two or more speakers, a few minutes long). Record your next call
                    with the meeting bot and it will show up here automatically.
                  </p>
                </div>
              )}

              {/* Summary tiles */}
              {data.summary.identifiedCalls > 0 && (
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
                  <StatTile
                    label="Avg talk ratio"
                    value={data.summary.avgTalkRatioPct != null ? `${data.summary.avgTalkRatioPct}%` : '—'}
                    valueColor={ratioColor(data.summary.avgTalkRatioPct)}
                    sub="Your share of speaking time. Under 45% is healthy discovery."
                  />
                  <StatTile
                    label="Longest monologue"
                    value={data.summary.longestMonologue ? fmtClock(data.summary.longestMonologue.seconds) : '—'}
                    sub={data.summary.longestMonologue
                      ? `on ${data.summary.longestMonologue.prospectName || 'a call'} — brief interjections don't reset the clock`
                      : 'No uninterrupted runs measured yet'}
                  />
                  <StatTile
                    label="Questions asked"
                    value={data.summary.avgQuestionsPer10Min != null ? data.summary.avgQuestionsPer10Min : '—'}
                    sub="Per 10 minutes of your speech, averaged per call"
                  />
                  <StatTile
                    label="Calls measured"
                    value={`${data.summary.identifiedCalls}/${data.summary.coachableCalls}`}
                    sub="Identified you / coachable calls in period"
                  />
                </div>
              )}

              {/* Per-call trend — chronological, bar = talk ratio */}
              {data.calls.length > 0 && (
                <div className="dash-card" style={{ padding: 18, marginBottom: 16 }}>
                  <h3 style={{ margin: '0 0 2px', fontSize: 14 }}>Talk ratio by call</h3>
                  <p style={{ margin: '0 0 12px', fontSize: 11, color: 'var(--room-text-subtle, #94a3b8)' }}>
                    Trend against your own baseline — not a leaderboard. Click a call to review it.
                  </p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {data.calls.map((c) => (
                      <button
                        key={c.demoId}
                        type="button"
                        onClick={() => { if (onOpenCall) onOpenCall(c.demoId); }}
                        title={`${c.questionCount == null ? '—' : c.questionCount} questions · longest monologue ${fmtClock(c.longestMonologueSeconds)}`}
                        style={{
                          display: 'grid', gridTemplateColumns: 'minmax(120px, 220px) 1fr 52px',
                          alignItems: 'center', gap: 10, padding: '4px 6px',
                          border: 'none', background: 'none', cursor: onOpenCall ? 'pointer' : 'default',
                          fontFamily: 'inherit', textAlign: 'left', borderRadius: 6,
                        }}
                      >
                        <span style={{ fontSize: 12, color: 'var(--room-text-body, #334155)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {c.prospectName || c.filename || 'Call'}
                          <span style={{ color: 'var(--room-text-subtle, #94a3b8)', marginLeft: 6 }}>
                            {new Date(c.createdAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                          </span>
                        </span>
                        <span style={{ height: 8, borderRadius: 4, background: 'var(--room-border-soft, #f1f5f9)', overflow: 'hidden' }}>
                          <span style={{
                            display: 'block', height: '100%', borderRadius: 4,
                            width: `${Math.min(100, c.talkRatioPct || 0)}%`,
                            background: ratioColor(c.talkRatioPct),
                            opacity: 0.85,
                          }} />
                        </span>
                        <span style={{ fontSize: 12, fontWeight: 600, color: ratioColor(c.talkRatioPct), textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                          {c.talkRatioPct != null ? `${c.talkRatioPct}%` : '—'}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Which one is you? — the identity backfill loop */}
              {data.needsIdentity.length > 0 && (
                <div className="dash-card" style={{ padding: 18, borderLeft: '3px solid #8b5cf6' }}>
                  <h3 style={{ margin: '0 0 2px', fontSize: 14 }}>
                    {viewingSelf ? 'Which one is you?' : `${data.user.name} hasn't identified themselves on these calls`}
                  </h3>
                  <p style={{ margin: '0 0 12px', fontSize: 11, color: 'var(--room-text-subtle, #94a3b8)' }}>
                    {viewingSelf
                      ? `We don't guess who's who on a call — pick yourself once per call and the metrics above update. ${data.needsIdentity.length} call${data.needsIdentity.length === 1 ? '' : 's'} waiting.`
                      : 'Their metrics will fill in once they pick themselves on each call.'}
                  </p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                    {data.needsIdentity.map((c) => (
                      <div key={c.demoId}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--room-text-body, #334155)', marginBottom: 6 }}>
                          {c.prospectName || c.filename || 'Call'}
                          <span style={{ color: 'var(--room-text-subtle, #94a3b8)', fontWeight: 400, marginLeft: 6 }}>
                            {new Date(c.createdAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                          </span>
                        </div>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                          {c.candidates.map((cand) => (
                            <button
                              key={cand.speaker}
                              type="button"
                              disabled={!viewingSelf || claiming === c.demoId}
                              onClick={() => claimSpeaker(c.demoId, cand.speaker)}
                              style={{
                                flex: '1 1 220px', maxWidth: 340, textAlign: 'left',
                                padding: '10px 12px', borderRadius: 10,
                                border: '1px solid var(--room-border, #e2e8f0)', background: 'var(--room-card, #fff)',
                                cursor: viewingSelf ? 'pointer' : 'default',
                                fontFamily: 'inherit',
                                opacity: claiming === c.demoId ? 0.6 : 1,
                              }}
                            >
                              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--room-text-strong, #0f172a)' }}>
                                {/* Timeline-attributed calls carry real
                                    participant names — picking "Dan
                                    Gonzalez" beats picking "Speaker 2". */}
                                {cand.name || `Speaker ${cand.speaker}`} · {cand.pct}% of the talking
                                {viewingSelf && <span style={{ color: '#6366f1', float: 'right' }}>That's me</span>}
                              </div>
                              {cand.sample && (
                                <div style={{ fontSize: 11, color: 'var(--room-text-muted, #64748b)', marginTop: 4, fontStyle: 'italic', lineHeight: 1.5 }}>
                                  “{cand.sample}…”
                                </div>
                              )}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
